"use client";

import { useRef, useState } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { AnimatePresence, motion } from "framer-motion";
gsap.registerPlugin(useGSAP, ScrollTrigger);
type Node = {
  id: string;
  name: string;
  region: string;
  x: number;
  y: number;
  size: number;
};
const nodes: Node[] = [{
  id: "n1",
  name: "Lisboa",
  region: "Portugal",
  x: 420,
  y: 190,
  size: 9
}, {
  id: "n2",
  name: "Porto",
  region: "Portugal",
  x: 425,
  y: 160,
  size: 8
}, {
  id: "n3",
  name: "Braga",
  region: "Portugal",
  x: 430,
  y: 150,
  size: 7
}, {
  id: "n4",
  name: "Coimbra",
  region: "Portugal",
  x: 425,
  y: 175,
  size: 7
}, {
  id: "n5",
  name: "Faro",
  region: "Portugal",
  x: 435,
  y: 210,
  size: 7
}, {
  id: "n6",
  name: "Évora",
  region: "Portugal",
  x: 440,
  y: 195,
  size: 7
}, {
  id: "n7",
  name: "Aveiro",
  region: "Portugal",
  x: 420,
  y: 165,
  size: 7
}, {
  id: "n8",
  name: "Leiria",
  region: "Portugal",
  x: 425,
  y: 180,
  size: 6
}, {
  id: "n9",
  name: "Setúbal",
  region: "Portugal",
  x: 425,
  y: 200,
  size: 7
}, {
  id: "n10",
  name: "Viseu",
  region: "Portugal",
  x: 435,
  y: 165,
  size: 6
}, {
  id: "n11",
  name: "Guarda",
  region: "Portugal",
  x: 445,
  y: 170,
  size: 6
}, {
  id: "n12",
  name: "Beja",
  region: "Portugal",
  x: 435,
  y: 205,
  size: 6
}, {
  id: "n13",
  name: "Castelo Branco",
  region: "Portugal",
  x: 440,
  y: 180,
  size: 6
}, {
  id: "n14",
  name: "Portalegre",
  region: "Portugal",
  x: 445,
  y: 190,
  size: 6
}];
const arcs = [{
  from: "n1",
  to: "n2"
}, {
  from: "n1",
  to: "n5"
}, {
  from: "n2",
  to: "n3"
}, {
  from: "n2",
  to: "n7"
}, {
  from: "n1",
  to: "n9"
}, {
  from: "n7",
  to: "n4"
}, {
  from: "n4",
  to: "n10"
}, {
  from: "n10",
  to: "n11"
}, {
  from: "n1",
  to: "n6"
}, {
  from: "n6",
  to: "n14"
}, {
  from: "n14",
  to: "n13"
}, {
  from: "n6",
  to: "n12"
}];
const regionSummary = [{
  label: "Norte",
  count: "4 centros"
}, {
  label: "Centro",
  count: "5 centros"
}, {
  label: "Sul",
  count: "5 centros"
}];
function nodeById(id: string) {
  return nodes.find(n => n.id === id)!;
}
function arcPath(x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dr = Math.sqrt(dx * dx + dy * dy) * 1.4;
  return `M ${x1} ${y1} A ${dr} ${dr} 0 0 1 ${x2} ${y2}`;
}
export default function OperationsMapVisualization() {
  const container = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<Node | null>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".reach-headline", {
        y: 24
      });
      gsap.set(".reach-info", {
        y: 24
      });
      gsap.set(".reach-map", {
        y: 32
      });
      gsap.to(".reach-headline", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 78%",
          once: true
        }
      });
      gsap.to(".reach-info", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        stagger: 0.12,
        ease: "power3.out",
        delay: 0.15,
        scrollTrigger: {
          trigger: el,
          start: "top 78%",
          once: true
        }
      });
      gsap.to(".reach-map", {
        opacity: 1,
        y: 0,
        duration: 1.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 78%",
          once: true
        }
      });
      const arcPaths = el.querySelectorAll<SVGPathElement>(".reach-arc");
      arcPaths.forEach(p => {
        const len = p.getTotalLength();
        gsap.set(p, {
          strokeDasharray: len,
          strokeDashoffset: len,
          opacity: 1
        });
      });
      gsap.to(arcPaths, {
        strokeDashoffset: 0,
        duration: 1.6,
        ease: "power2.inOut",
        stagger: 0.08,
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          once: true
        }
      });
      gsap.to(".reach-node-glow", {
        opacity: 1,
        scale: 1.35,
        transformOrigin: "center center",
        duration: 1.2,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        stagger: {
          each: 0.12,
          from: "random"
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".reach-headline", ".reach-info", ".reach-map"], {
        opacity: 1,
        y: 0
      });
      gsap.set(".reach-arc", {
        opacity: 1,
        strokeDashoffset: 0
      });
      gsap.set(".reach-node-glow", {
        opacity: 0.6
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="26932" id="operationsmapvisualization" ref={container} className="bg-background text-foreground relative overflow-hidden py-32 md:py-44">
      {}
      <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="h-[720px] w-[720px] rounded-lg blur-3xl" style={{
        background: "radial-gradient(circle, hsl(215 80% 20% / 0.08) 0%, transparent 65%)"
      }} />
      </div>

      <div className="relative mx-auto max-w-[1360px] px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {}
          <div className="reach-map lg:col-span-8" style={{
          opacity: 0
        }}>
            <div className="relative min-h-[400px] w-full overflow-hidden border md:min-h-[560px]" style={{
            borderRadius: "24px",
            borderColor: "hsl(210 10% 85%)",
            background: "linear-gradient(180deg, hsl(210 20% 98%) 0%, hsl(210 10% 92%) 100%)",
            backdropFilter: "blur(24px)",
            boxShadow: "inset 0 0 0 1px hsl(210 10% 85%)"
          }}>
              <svg viewBox="0 0 900 480" className="h-full w-full" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Mapa de operações da Eurocontrol em Portugal">
                <defs>
                  <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="hsl(215 80% 20%)" stopOpacity="0.55" />
                    <stop offset="100%" stopColor="hsl(215 80% 20%)" stopOpacity="0" />
                  </radialGradient>
                  <filter id="nodeBlur" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="3" />
                  </filter>
                </defs>

                {}
                <g fill="none" stroke="hsl(210 10% 85%)" strokeWidth="1" strokeLinejoin="round" opacity="0.9">
                  <path d="M400,140 L450,140 L460,220 L410,220 Z" />
                </g>

                {}
                <g fill="none" strokeLinecap="round">
                  {arcs.map((arc, i) => {
                  const a = nodeById(arc.from);
                  const b = nodeById(arc.to);
                  return <path key={`${arc.from}-${arc.to}`} data-index={i} className="reach-arc" d={arcPath(a.x, a.y, b.x, b.y)} stroke="hsl(215 80% 20% / 0.35)" strokeWidth="1" style={{
                    opacity: 0
                  }} />;
                })}
                </g>

                {}
                <g>
                  {nodes.map((n, i) => {
                  const isActive = hovered?.id === n.id;
                  return <g key={n.id} data-index={i} onMouseEnter={() => setHovered(n)} onMouseLeave={() => setHovered(null)} onFocus={() => setHovered(n)} onBlur={() => setHovered(null)} tabIndex={0} role="button" aria-label={`${n.name}, ${n.region}`} className="cursor-pointer outline-none" style={{
                    transition: "transform 200ms ease"
                  }}>
                        {}
                        <circle cx={n.x} cy={n.y} r={n.size + 6} fill="url(#nodeGlow)" className="reach-node-glow" style={{
                      opacity: isActive ? 1 : 0.4,
                      filter: "url(#nodeBlur)"
                    }} />
                        {}
                        <circle cx={n.x} cy={n.y} r={isActive ? n.size + 14 : n.size + 2} fill="hsl(215 80% 20%)" opacity={isActive ? 0.18 : 0} style={{
                      transition: "all 200ms ease-out"
                    }} />
                        {}
                        <circle cx={n.x} cy={n.y} r={n.size / 2} fill="hsl(215 80% 20%)" style={{
                      filter: "drop-shadow(0 0 6px hsl(215 80% 20% / 0.8))"
                    }} />
                        {}
                        <circle cx={n.x} cy={n.y} r={14} fill="transparent" />
                      </g>;
                })}
                </g>
              </svg>

              {}
              <AnimatePresence>
                {hovered && <motion.div key={hovered.id} initial={{
                opacity: 0,
                y: 4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 4
              }} transition={{
                duration: 0.2,
                ease: [0.22, 1, 0.36, 1]
              }} className="pointer-events-none absolute z-10" style={{
                left: `${hovered.x / 900 * 100}%`,
                top: `${hovered.y / 480 * 100}%`,
                transform: "translate(-50%, -140%)"
              }}>
                    <div className="font-mono text-xs tracking-wide whitespace-nowrap border px-3 py-1.5" style={{
                  color: "var(--foreground)",
                  background: "hsl(0 0% 100% / 0.9)",
                  borderColor: "hsl(215 80% 20% / 0.4)",
                  borderRadius: "10px",
                  backdropFilter: "blur(12px)",
                  boxShadow: "0 8px 24px hsl(220 22% 6% / 0.1), inset 0 0 0 1px hsl(210 10% 85%)"
                }}>
                      <span style={{
                    color: "hsl(215 80% 20%)"
                  }}>
                        {hovered.region}
                      </span>
                      <span className="mx-2" style={{
                    color: "hsl(215 12% 40%)"
                  }}>
                        {"//"}
                      </span>
                      <span>{hovered.name}</span>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {}
          <div className="lg:col-span-4 flex flex-col justify-center">
            <div className="reach-info flex items-center gap-2 mb-6" style={{
            opacity: 0
          }}>
              <span className="inline-block h-1.5 w-1.5 rounded-lg" style={{
              background: "hsl(215 80% 20%)",
              boxShadow: "0 0 10px hsl(215 80% 20% / 0.6)"
            }} />
              <span className="font-mono text-xs font-medium uppercase" style={{
              letterSpacing: "0.22em",
              color: "var(--muted-foreground)"
            }}>
                {"Presença Nacional"}
              </span>
            </div>

            <h2 className="reach-headline font-default font-medium leading-[1.02] mb-6" style={{
            opacity: 0,
            fontSize: "clamp(1.75rem, 2.4vw + 1rem, 2.75rem)",
            letterSpacing: "-0.03em",
            color: "var(--foreground)"
          }}>
              <span style={{
              textDecoration: "underline",
              textDecorationColor: "hsl(215 90% 45%)",
              textDecorationThickness: "0.08em",
              textUnderlineOffset: "0.14em"
            }}>
                {"Cobertura"}
              </span>
              {" em todo o país."}
            </h2>

            <p className="reach-info font-default text-base md:text-lg leading-relaxed mb-10 max-w-md" style={{
            opacity: 0,
            color: "var(--muted-foreground)"
          }}>
              {"Soluções de engenharia e pulverização industrial com suporte técnico especializado."}
            </p>

            <ul className="reach-info flex flex-col gap-3" style={{
            opacity: 0
          }}>
              {regionSummary.map((r, i) => <li key={r.label} data-index={i} className="flex items-center gap-3 border py-3 px-4" style={{
              borderColor: "hsl(210 10% 85%)",
              background: "hsl(210 10% 92% / 0.4)",
              borderRadius: "14px",
              backdropFilter: "blur(12px)"
            }}>
                  <span className="inline-block h-2 w-2 rounded-lg flex-shrink-0" style={{
                background: "hsl(215 80% 20%)",
                boxShadow: "0 0 8px hsl(215 80% 20% / 0.7)"
              }} />
                  <span className="font-mono text-sm" style={{
                color: "var(--foreground)",
                letterSpacing: "0.02em"
              }}>
                    {r.label}
                  </span>
                  <span className="font-mono text-sm ml-auto" style={{
                color: "var(--muted-foreground)",
                letterSpacing: "0.02em"
              }}>
                    {r.count}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}